'use client';

import { useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  LogOut,
  LockKeyhole,
  Check,
  ArrowRightLeft,
  Star,
  Smile,
} from 'lucide-react';
import NotificationIcon from '/public/notification.svg';
import LogoSvg from '/public/logo-1.svg';
import IconShowSvg from '/public/icon-show.svg';
import SpaceSvg from '/public/left-icon-space.svg';
import PrivacyAgreementSvg from '/public/privacy-agreement.svg';
import { Loading } from '@/components/ui/loading';
import AddIcon from '/public/add.svg';
import RightArrowIcon from '/public/right-arrow-medium.svg';
import KeyIcon from '/public/key.svg';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '../ui/switch';
import {
  isDefaultNotifyPermission,
  requestNotifyPermission,
} from '@/lib/utils/utils';
import {
  getUserSpaceList,
  IUserSpaceList,
  changeSpace,
  updateUserConfig,
} from '@/services/user';
import { triggerPointsDetailsModal } from '@/components/deep-mining/points-details-modal';
import { cn } from '@/lib/utils';
import { maskEmail, maskPhoneNumber } from '@/lib/utils/form';
import { usePermissionCheck } from '@/hooks/use-permission-check';
import { authService } from '@/services/auth-service';
import useUserConfig from '@/hooks/deep-mining/use-user-config';
import { GenDeviceKeyModel } from './gen-device-key-model';
import { triggerAccessKeyModal } from './access-key-modal';
import { authChannelPostMessage } from '@/lib/channel';
import { useStores } from '@/store/hooks/useStore';
import { eventBus, EventTypes } from '@/lib/event-bus';
import { useI18n } from '@/hooks/use-i18n';
import { LanguageSelector } from '@/components/sidebar/language-selector';

export function NavUserWithTeams({
  userInfo,
  collapsed,
  onChangeUserName,
  onChangePassword,
  handleLogoutSpace,
  onOpenPrivacyAgreement,
}: {
  userInfo: USER.Profile;
  collapsed?: boolean;
  onChangeUserName?: () => void;
  onChangePassword?: () => void;
  handleLogoutSpace?: () => void;
  onOpenPrivacyAgreement?: () => void;
}) {
  const { t } = useI18n(['components', 'common']);
  const router = useRouter();
  const { isMobile } = useSidebar();
  const [showNewTeamDialog, setShowNewTeamDialog] = useState(false);
  const { userConfig, getUserConfig } = useUserConfig();
  const [threadNotificationFlag, setThreadNotificationFlag] = useState(
    userConfig?.thread_notification ?? true,
  );
  const stores = useStores();
  const [userSpaces, setUserSpaces] = useState<IUserSpaceList[]>([]);
  const [switchingSpace, setSwitchingSpace] = useState(false); // 切换空间loading状态
  const currentSpace = userInfo?.active_space; // 获取当前活跃空间
  const canManageSpace = usePermissionCheck('space:manage');
  useEffect(() => {
    getUserConfig(['thread_notification']);
  }, []);
  useEffect(() => {
    setThreadNotificationFlag(userConfig?.thread_notification ?? true);
  }, [userConfig?.thread_notification]);

  // 获取用户空间列表
  useEffect(() => {
    const fetchUserSpaces = async () => {
      try {
        const spaces = await getUserSpaceList();
        setUserSpaces(spaces || []);
      } catch (error) {
        console.error('获取用户团队列表失败:', error);
      } finally {
      }
    };

    fetchUserSpaces();
  }, []);

  // 处理用户空间数据
  const spaceGroups = [
    {
      type: 'person',
      label: t('components:sidebar.personalSpace'),
      spaces: userSpaces.filter((space) => space.space_type === 'person'),
    },
    {
      type: 'team',
      label: t('common:team'),
      spaces: userSpaces.filter((space) => space.space_type !== 'person'),
    },
  ].filter((group) => group.spaces.length > 0);

  const handleLogout = async () => {
    try {
      await authService.logout();
      router.push('/auth');

      authChannelPostMessage({
        type: 'logout',
        payload: null,
      });
    } catch (error) {
      console.error('退出登录失败:', error);
    }
  };

  // 切换空间
  const handleSwitchSpace = async (spaceId: string) => {
    if (spaceId === userInfo?.active_space?.id) {
      return;
    }
    setSwitchingSpace(true);
    try {
      await changeSpace({ space_id: spaceId });
      window.DM_SPACE_ID = '';

      // 空间切换后，立即获取用户信息和语言设置
      await stores.userStore.updateUserInfo(true);

      // 派发空间切换
      authChannelPostMessage({
        type: 'spaceChange',
        payload: {
          spaceId: spaceId,
        },
      });
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('切换团队失败:', error);
    } finally {
      setSwitchingSpace(false);
    }
  };

  // 渲染空间列表
  const renderSpaceItem = (space: IUserSpaceList, type: string) => {
    const isActive = space.id === currentSpace?.id;
    const isPersonal = type === 'person';

    return (
      <DropdownMenuItem
        key={space.id}
        onClick={() => handleSwitchSpace(space.id)}
        className="gap-2 my-1 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
      >
        <div
          className={cn(
            'w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-200',
            isActive ? 'bg-[#F1F1FD]' : 'bg-[#F4F4F5] hover:bg-[#E5E5E5]',
          )}
        >
          {isPersonal ? (
            <IconShowSvg
              className={cn(
                '!w-3 !h-3 flex-shrink-0 transition-colors duration-200',
                isActive ? 'text-primary' : 'text-[#212121] hover:text-primary',
              )}
            />
          ) : (
            <span
              className={cn(
                'text-[11px] font-[600] transition-colors duration-200',
                isActive ? 'text-primary' : 'text-[#212121] hover:text-primary',
              )}
            >
              {space.space_name?.slice(0, 1)}
            </span>
          )}
        </div>
        <span
          className={cn(
            'flex-1 text-[13px] truncate transition-colors duration-200',
            isActive
              ? 'text-primary font-bold'
              : 'text-gray-75 hover:text-primary',
          )}
        >
          {space.space_name}
        </span>
        {isActive && <Check className="w-4 h-4 text-primary" />}
      </DropdownMenuItem>
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  const account = userInfo.email
    ? maskEmail(userInfo.email)
    : maskPhoneNumber(userInfo.phone);

  // 渲染空间头像
  const renderSpaceAvatar = () => {
    return (
      <div className="w-8 h-8 rounded-full bg-[#F1F1FD] flex items-center justify-center border border-white flex-shrink-0">
        {currentSpace?.space_type === 'person' ? (
          <LogoSvg className="text-primary" />
        ) : (
          <span className="text-[11px] font-semibold text-primary">
            {currentSpace?.space_name?.slice(0, 1)}
          </span>
        )}
      </div>
    );
  };

  // 渲染空间按钮的左侧内容
  const renderSpaceLeftContent = () => {
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {renderSpaceAvatar()}
        <span className="truncate text-[13px] min-w-0 text-[rgba(0,0,0,0.75)]">
          {currentSpace?.space_name}
        </span>
      </div>
    );
  };

  // 渲染空间按钮的右侧内容
  const renderSpaceRightContent = (isPersonalSpace: boolean) => {
    if (isPersonalSpace) {
      return (
        <Link href="/create-space" target="_blank">
          <span className="text-[13px] text-primary group-data-[collapsible=icon]:hidden">
            {t('components:sidebar.createTeam')}
          </span>
        </Link>
      );
    }

    if (switchingSpace) {
      return (
        <Loading className="h-3 w-3 group-data-[collapsible=icon]:hidden" />
      );
    }

    return (
      <ArrowRightLeft className="!h-3! !w-3 flex-shrink-0 text-[#212121] group-data-[collapsible=icon]:hidden cursor-pointer hover:text-[#7171EE] transition-colors duration-200" />
    );
  };

  // 渲染统一的空间按钮
  const renderSpaceButton = (isPersonalSpace: boolean) => {
    const buttonClassName = cn(
      'h-10 bg-white cursor-pointer group-data-[collapsible=icon]:rounded-full!',
      isPersonalSpace ? 'border border-white' : '',
    );

    return (
      <div className="flex relative items-center justify-between">
        <div className={cn('flex-1 min-w-0', collapsed ? '' : 'px-2')}>
          <SidebarMenuButton asChild size="md" className={buttonClassName}>
            <div className="flex items-center justify-between min-w-0">
              {renderSpaceLeftContent()}
              {renderSpaceRightContent(isPersonalSpace)}
            </div>
          </SidebarMenuButton>
        </div>
      </div>
    );
  };

  // 渲染团队信息组件
  const renderTeamInfo = () => {
    // 直接使用 active_space 的信息
    if (!currentSpace?.space_type) return null;
    // 判断用户是否只有个人空间（没有团队空间）
    const isPersonalSpace =
      userSpaces.length > 0 &&
      userSpaces.every((space) => space.space_type === 'person');

    return (
      <SidebarMenuItem className="mb-1">
        {isPersonalSpace ? (
          // 只有个人空间 - 直接点击跳转
          renderSpaceButton(true)
        ) : (
          // 团队空间 - 显示下拉菜单
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {renderSpaceButton(false)}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-50 max-w-50 rounded-lg py-3 px-2"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              {/*渲染空间列表 */}
              {spaceGroups.map((group) => (
                <div key={group.type}>
                  <DropdownMenuLabel className="text-[11px] text-[rgba(0,0,0,0.45)] py-0">
                    {group.label}
                  </DropdownMenuLabel>
                  {group.spaces.map((space) =>
                    renderSpaceItem(space, group.type),
                  )}
                </div>
              ))}

              {/* 创建团队 */}
              <DropdownMenuItem
                onClick={() => window.open('/create-space', '_blank')}
                className="gap-2 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-[#F1F1FD] group-hover:bg-primary/10 transition-colors duration-200">
                  <AddIcon className="w-2 h-2 text-primary" />
                </div>
                <span className="flex-1 text-[13px] text-primary hover:text-primary/80 transition-colors duration-200">
                  {t('components:sidebar.createTeam')}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarMenuItem>
    );
  };

  useEffect(() => {
    getUserConfig(['thread_notification']);

    const fetchUserSpaces = async () => {
      try {
        const spaces = await getUserSpaceList();
        setUserSpaces(spaces || []);
      } catch (error) {
        console.error('获取用户空间列表失败:', error);
      } finally {
      }
    };

    fetchUserSpaces();
  }, []);

  useEffect(() => {
    setThreadNotificationFlag(userConfig?.thread_notification ?? true);
  }, [userConfig?.thread_notification]);

  return (
    <Dialog open={showNewTeamDialog} onOpenChange={setShowNewTeamDialog}>
      {/* 已经移除 */}
      {/* <GenDeviceKeyModel
        isShow={isShowDeviceConnectionModel}
        onClose={() => setIsShowDeviceConnectionModel(false)}
      /> */}

      <SidebarMenu>
        {/* 团队信息组件 */}
        {renderTeamInfo()}

        <SidebarMenuItem>
          <DropdownMenu>
            <div className="flex relative items-center justify-between">
              <DropdownMenuTrigger
                className={cn('flex-1 min-w-0', collapsed ? '' : 'px-2')}
              >
                <SidebarMenuButton
                  asChild
                  size="lg"
                  className="bg-white cursor-pointer group-data-[collapsible=icon]:rounded-full!"
                >
                  <div className="flex items-center justify-between min-w-0">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Avatar className="h-8 w-8 rounded-lg flex-shrink-0">
                        <AvatarImage src={'/user.svg'} alt={userInfo.name} />
                        <AvatarFallback className="rounded-lg">
                          {getInitials(userInfo.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate text-[13px] min-w-0 text-[rgba(0,0,0,0.75)] mb-1">
                          {userInfo.name}
                        </span>
                        <span className="truncate text-[11px] text-[rgba(0,0,0,0.45)]">
                          {account}
                        </span>
                      </div>
                    </div>
                    <RightArrowIcon className="!h-3 !w-3 flex-shrink-0 text-[#212121] group-data-[collapsible=icon]:hidden -rotate-90 cursor-pointer hover:text-[#7171EE] transition-colors duration-200" />
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-50 rounded-lg py-3 px-2"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuItem
                onClick={triggerAccessKeyModal}
                className="gap-2 my-1"
              >
                <KeyIcon className="h-4 w-4 text-gray-75" />
                <span className="text-gray-75 text-[13px]">
                  {t('components:sidebar.accessKey.menuItem')}
                </span>
              </DropdownMenuItem>

              {canManageSpace && (
                <DropdownMenuItem
                  onClick={() => window.open('/space-manage', '_blank')}
                  className="gap-2 my-1"
                >
                  <SpaceSvg className="h-4 w-4 text-gray-75" />
                  <span className="text-gray-75 text-[13px]">
                    {t('components:sidebar.backendManage')}
                  </span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={triggerPointsDetailsModal}
                className="gap-2 my-1"
              >
                <Star className="h-4 w-4 text-gray-75" />
                <span className="text-gray-75 text-[13px]">
                  {t('components:sidebar.pointsDetails')}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex justify-between items-center my-1"
                onSelect={(e) => e.preventDefault()}
              >
                <div className="flex items-center gap-2 ">
                  <NotificationIcon className="h-4 w-4 text-gray-75" />
                  <span className="text-gray-75 text-[13px]">
                    {t('components:sidebar.taskNotification')}
                  </span>
                </div>
                <Switch
                  checked={threadNotificationFlag as boolean}
                  onCheckedChange={(checked) => {
                    setThreadNotificationFlag(checked);
                    // 当开启的时候询问用户是否允许发送通知
                    if (checked && isDefaultNotifyPermission()) {
                      requestNotifyPermission();
                    }
                    updateUserConfig({
                      key: 'thread_notification',
                      value: String(checked),
                    });
                  }}
                  className="w-7 h-[14px] ml-2"
                  circleClassName="w-3 h-3"
                  checkedClassName="translate-x-3"
                />
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onChangeUserName}
                className="gap-2 my-1"
              >
                <Smile className="h-4 w-4 text-gray-75" />
                <span className="text-gray-75 text-[13px]">
                  {t('components:sidebar.modifyNickname')}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onChangePassword}
                className="gap-2 my-1"
              >
                <LockKeyhole className="h-4 w-4 text-gray-75" />
                <span className="text-gray-75 text-[13px]">
                  {t('components:sidebar.modifyPassword')}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onOpenPrivacyAgreement}
                className="gap-2"
              >
                <PrivacyAgreementSvg className="h-4 w-4 text-gray-75" />
                <span className="text-gray-75 text-[13px]">
                  {t('components:sidebar.privacyAgreement')}
                </span>
              </DropdownMenuItem>
              {/* 语言切换组件 */}
              <div>
                <LanguageSelector />
              </div>
              {currentSpace?.space_type !== 'person' && (
                <DropdownMenuItem
                  onClick={handleLogoutSpace}
                  className="gap-2 my-1"
                >
                  <LogOut className="h-4 w-4 text-gray-75" />
                  <span className="text-gray-75 text-[13px]">
                    {t('components:sidebar.exitCurrentTeam')}
                  </span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleLogout}
                className="gap-2 text-[red] my-1"
              >
                <LogOut className="h-4 w-4 text-[red]" />
                <span className="text-[red] text-[13px]">
                  {t('components:sidebar.logout')}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </Dialog>
  );
}
